export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function first_present(values, fallback = "") {
  return values.find((value) => value) || fallback;
}

function get_member_positions(member) {
  const positions = member?.positions || [];
  return Array.isArray(positions) ? positions : [];
}

function get_member_primary_position_id(member) {
  const position = get_member_positions(member)[0];
  return position?.id || position?.position_id || position?.organization_position_id || "";
}

function get_member_permission_ids(member) {
  const permissions = member?.organization_permissions || member?.permissions || [];
  if (!Array.isArray(permissions)) return [];
  return permissions.map((permission) => permission.id || permission.code || permission.name || permission).filter(Boolean);
}

function get_member_permissions(member) {
  const permissions = member?.organization_permissions || member?.permissions || [];
  return Array.isArray(permissions) ? permissions : [];
}

function normalize_value(value) {
  return String(value || "").trim().toLowerCase();
}

export function get_member_permissions_for_feature(member, feature_key = null) {
  const permissions = get_member_permissions(member);
  const safe_feature_key = normalize_value(feature_key);
  if (!safe_feature_key) return permissions;

  return permissions.filter((permission) =>
    normalize_value(permission?.feature?.key) === safe_feature_key ||
    normalize_value(permission?.feature?.name) === safe_feature_key
  );
}

function get_member_feature_id_value(permission) {
  return String(permission?.feature?.id || permission?.feature_id || "").trim();
}

function get_member_permission_id_value(permission) {
  return first_present([
    permission?.permission?.id,
    permission?.id,
    permission?.code,
    permission?.name,
    permission,
  ], "");
}

export function get_member_permission_ids_for_feature(member, feature_id) {
  const permissions = get_member_permissions(member);
  const safe_feature_id = String(feature_id || "").trim();
  return permissions
    .filter((permission) => !safe_feature_id || get_member_feature_id_value(permission) === safe_feature_id)
    .map(get_member_permission_id_value)
    .filter(Boolean);
}

export function get_member_feature_id(member) {
  const permission = get_member_permissions(member)[0];
  return first_present([permission?.feature?.id, permission?.feature_id], "");
}

function member_matches_email(member, email) {
  return !!email && normalize_value(member?.email) === email;
}

function member_matches_user_id(member, user_id) {
  return !!user_id && String(member?.id || "").trim() === user_id;
}

export function get_organization_member_by_user_details(members, user_details = {}) {
  const safe_members = Array.isArray(members) ? members : [];
  const email = normalize_value(user_details?.email);
  const user_id = String(user_details?.user_id || "").trim();

  return safe_members.find((member) =>
    member_matches_email(member, email) || member_matches_user_id(member, user_id)
  ) || null;
}

export function member_has_feature_permission(member, feature_key) {
  return get_member_permissions_for_feature(member, feature_key).length > 0;
}

export function normalize_organisation_feature(feature) {
  return {
    ...feature,
    id: first_present([feature?.id, feature?.feature_id], null),
    name: first_present([feature?.name, feature?.label, feature?.key], ""),
    key: first_present([feature?.key], ""),
    permissions: Array.isArray(feature?.permissions) ? feature.permissions : null,
  };
}

export function get_initials(member) {
  const first = member?.first_name?.charAt(0) || "";
  const last = member?.last_name?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "U";
}

export function get_member_profile_picture(member) {
  if (!member) return null;
  const profile_files = member.user?.profile?.profile_files || member.profile?.profile_files || [];
  const profile_picture = profile_files.find((file) => file.profile_file_type === "profile_picture");
  return first_present([
    member.profile_picture,
    member.user?.profile_picture,
    profile_picture?.file?.s3_url,
    profile_picture?.file?.url,
    profile_picture?.url,
  ], null);
}

export {
  get_member_positions,
  get_member_primary_position_id,
  get_member_permission_ids,
};
